import type { StorageSample } from "../../shared/storage.js";
import { h } from "../dom.js";
import { compact } from "../format.js";

const SVG = "http://www.w3.org/2000/svg";
const W = 900;
const H = 260;
const PAD = { left: 40, right: 14, top: 12, bottom: 26 };

function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, text?: string): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  if (text !== undefined) el.textContent = text;
  return el;
}

const pct = (used: number, total: number) => (total > 0 ? (used / total) * 100 : 0);

/** Share of the bytes and of the types used at each read, on one 0–100% axis, with a crosshair tooltip. */
export function storageChart(samples: readonly StorageSample[]): HTMLElement {
  const wrap = h("div", { class: "chart storage-chart" });
  if (samples.length < 2) {
    wrap.append(h("p", { class: "muted empty" }, "The chart fills in as the cells are read (every 10 minutes while the game is connected)."));
    return wrap;
  }
  const from = samples[0].at;
  const to = samples[samples.length - 1].at;
  const span = Math.max(1, to - from);
  const x = (at: number) => PAD.left + ((at - from) / span) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + ((100 - v) / 100) * (H - PAD.top - PAD.bottom);

  const svg = s("svg", { viewBox: `0 0 ${W} ${H}`, role: "img", "aria-label": "Share of storage bytes and types used over time" });
  for (const v of [0, 25, 50, 75, 100]) {
    svg.append(s("line", { class: v === 0 ? "baseline" : "grid", x1: PAD.left, x2: W - PAD.right, y1: y(v), y2: y(v) }));
    svg.append(s("text", { class: "axis", x: PAD.left - 6, y: y(v) + 4, "text-anchor": "end" }, `${v}%`));
  }
  const days = span > 2 * 86_400_000;
  for (let i = 0; i <= 4; i++) {
    const at = from + (span * i) / 4;
    const d = new Date(at);
    const text = days ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    svg.append(s("text", { class: "axis", x: x(at), y: H - 8, "text-anchor": i === 0 ? "start" : i === 4 ? "end" : "middle" }, text));
  }
  const line = (cls: string, value: (p: StorageSample) => number) =>
    s("path", { class: cls, d: samples.map((p, i) => `${i ? "L" : "M"}${x(p.at).toFixed(1)},${y(value(p)).toFixed(1)}`).join("") });
  const bytes = (p: StorageSample) => pct(p.usedBytes, p.totalBytes);
  const types = (p: StorageSample) => pct(p.usedTypes, p.totalTypes);
  svg.append(line("line-bytes", bytes), line("line-types", types));

  const cross = s("line", { class: "crosshair", y1: PAD.top, y2: H - PAD.bottom, x1: 0, x2: 0, visibility: "hidden" });
  const dotB = s("circle", { class: "dot-bytes", r: 4, visibility: "hidden" });
  const dotT = s("circle", { class: "dot-types", r: 4, visibility: "hidden" });
  const hit = s("rect", { class: "hit", x: PAD.left, y: 0, width: W - PAD.left - PAD.right, height: H });
  svg.append(cross, dotB, dotT, hit);
  const tip = h("div", { class: "chart-tip", hidden: true });
  const legend = h(
    "div",
    { class: "chart-legend small" },
    h("span", null, h("i", { class: "swatch in" }), "Bytes used"),
    h("span", null, h("i", { class: "swatch out" }), "Types used"),
  );
  wrap.append(legend, svg, tip);

  hit.addEventListener("pointermove", (ev) => {
    const box = svg.getBoundingClientRect();
    const at = from + ((((ev.clientX - box.left) / box.width) * W - PAD.left) / (W - PAD.left - PAD.right)) * span;
    let p = samples[0];
    for (const q of samples) if (Math.abs(q.at - at) < Math.abs(p.at - at)) p = q;
    const px = x(p.at);
    for (const [el, cy] of [[cross, null], [dotB, y(bytes(p))], [dotT, y(types(p))]] as const) {
      el.setAttribute("visibility", "visible");
      if (cy === null) {
        el.setAttribute("x1", String(px));
        el.setAttribute("x2", String(px));
      } else {
        el.setAttribute("cx", String(px));
        el.setAttribute("cy", String(cy));
      }
    }
    tip.hidden = false;
    tip.replaceChildren(
      h("strong", null, new Date(p.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })),
      h("span", null, h("i", { class: "swatch in" }), `Bytes ${bytes(p).toFixed(1)}% (${compact(p.usedBytes)} of ${compact(p.totalBytes)})`),
      h("span", null, h("i", { class: "swatch out" }), `Types ${types(p).toFixed(1)}% (${compact(p.usedTypes)} of ${compact(p.totalTypes)})`),
    );
    const left = (px / W) * box.width;
    tip.style.left = `${Math.max(0, Math.min(left + 12, box.width - 230))}px`;
    tip.style.top = `${(PAD.top / H) * box.height + 8}px`;
  });
  hit.addEventListener("pointerleave", () => {
    for (const el of [cross, dotB, dotT]) el.setAttribute("visibility", "hidden");
    tip.hidden = true;
  });
  return wrap;
}
